<template>
  <v-snackbar
    :model-value="modelValue"
    :timeout="timeout"
    location="bottom"
    rounded="pill"
    min-width="360"
    content-class="vsk-snackbar-content"
    class="vsk-snackbar"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div class="snackbar-shell" :data-tone="normalizedTone">
      <div class="snackbar-icon-wrap">
        <v-icon size="20">{{ icon }}</v-icon>
      </div>
      <div class="snackbar-content-wrap">
        <p class="snackbar-title">{{ title }}</p>
        <div class="snackbar-lines">
          <p v-for="(line, index) in lines" :key="index" class="snackbar-line">
            {{ line }}
          </p>
        </div>
      </div>
      <v-btn
        icon="mdi-close"
        size="x-small"
        variant="text"
        class="snackbar-close-btn"
        @click="$emit('update:modelValue', false)"
      />
    </div>
  </v-snackbar>
</template>

<script lang="ts">
import { computed } from "vue";
import { defineComponent } from "vue";
import { normalizeMultilineText } from "@/utils/feedback";

export default defineComponent({
  name: "AppSnackbar",
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    title: {
      type: String,
      default: "",
    },
    text: {
      type: String,
      default: "",
    },
    color: {
      type: String,
      default: "primary",
    },
    icon: {
      type: String,
      default: "mdi-bell-ring-outline",
    },
    timeout: {
      type: Number,
      default: 5200,
    },
  },
  emits: ["update:modelValue"],
  setup(props) {
    const lines = computed(() => normalizeMultilineText(props.text));
    const normalizedTone = computed(() => {
      const tone = String(props.color || "primary").toLowerCase();
      if (["success", "info", "warning", "error", "primary"].includes(tone)) {
        return tone;
      }
      return "primary";
    });
    return { lines, normalizedTone };
  },
});
</script>

<style scoped>
.vsk-snackbar :deep(.v-overlay__content) {
  margin-bottom: 24px;
}

.vsk-snackbar :deep(.v-snackbar__wrapper) {
  background: transparent;
  box-shadow: none;
}

.vsk-snackbar :deep(.v-snackbar__content) {
  padding: 0;
}

.snackbar-shell {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px 10px 10px;
  border-radius: 18px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: linear-gradient(
    140deg,
    rgba(15, 23, 42, 0.92),
    rgba(30, 41, 59, 0.9)
  );
  color: #f8fafc;
}

.snackbar-shell[data-tone="success"] {
  background: linear-gradient(
    140deg,
    rgba(6, 78, 59, 0.95),
    rgba(5, 150, 105, 0.88)
  );
}

.snackbar-shell[data-tone="error"] {
  background: linear-gradient(
    140deg,
    rgba(127, 29, 29, 0.95),
    rgba(220, 38, 38, 0.88)
  );
}

.snackbar-shell[data-tone="warning"] {
  background: linear-gradient(
    140deg,
    rgba(120, 53, 15, 0.95),
    rgba(234, 88, 12, 0.88)
  );
}

.snackbar-shell[data-tone="info"] {
  background: linear-gradient(
    140deg,
    rgba(30, 58, 138, 0.95),
    rgba(37, 99, 235, 0.88)
  );
}

.snackbar-icon-wrap {
  width: 34px;
  height: 34px;
  border-radius: 11px;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.16);
  flex-shrink: 0;
}

.snackbar-content-wrap {
  flex: 1;
  min-width: 0;
}

.snackbar-title {
  margin: 0 0 4px 0;
  font-size: 0.92rem;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.snackbar-lines {
  display: grid;
  gap: 4px;
}

.snackbar-line {
  margin: 0;
  line-height: 1.35;
  font-size: 0.86rem;
  color: rgba(248, 250, 252, 0.95);
}

.snackbar-close-btn {
  margin-top: 2px;
  color: rgba(255, 255, 255, 0.88);
}
</style>
